import React, { useState, useEffect } from 'react';
import { Filter, FilterType } from '../../types';

interface AddFilterModalProps {
    onClose: () => void;
    onSave: (filter: Omit<Filter, 'filterId' | 'companyId' | 'createdAt'> | Filter) => Promise<void>;
    filterToEdit?: Filter | null;
}

const AddFilterModal: React.FC<AddFilterModalProps> = ({ onClose, onSave, filterToEdit }) => {
    const [name, setName] = useState('');
    const [type, setType] = useState<FilterType>(FilterType.TEXT);
    const [options, setOptions] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isEditing = !!filterToEdit;

    useEffect(() => {
        if (isEditing && filterToEdit) {
            setName(filterToEdit.name);
            setType(filterToEdit.type);
            setOptions(filterToEdit.options?.join(', ') || '');
        }
    }, [filterToEdit, isEditing]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        const filterData: any = {
            name,
            type,
        };
        if (type === FilterType.DROPDOWN || type === FilterType.MULTI) {
            filterData.options = options.split(',').map(opt => opt.trim()).filter(Boolean);
        }
        if (isEditing && filterToEdit) {
            filterData.filterId = filterToEdit.filterId;
            filterData.companyId = filterToEdit.companyId;
            filterData.createdAt = filterToEdit.createdAt;
        }
        
        try {
            await onSave(filterData);
        } catch (error) {
            // Parent component handles the error alert.
            // The button will be re-enabled by the finally block.
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50">
            <div className="bg-gray-900 rounded-lg shadow-xl p-8 w-full max-w-md m-4">
                <h2 className="text-2xl font-bold text-white mb-6">{isEditing ? 'Edit Filter' : 'Add New Filter'}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Filter Name</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Filter Type</label>
                        <select value={type} onChange={(e) => setType(e.target.value as FilterType)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                            {Object.values(FilterType).map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
                        </select>
                    </div>
                    {(type === FilterType.DROPDOWN || type === FilterType.MULTI) && (
                         <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Options</label>
                            <input type="text" value={options} onChange={(e) => setOptions(e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. Option 1, Option 2, Option 3" />
                            <p className="text-xs text-gray-500 mt-1">Enter comma-separated values.</p>
                        </div>
                    )}
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-500">
                            {isSubmitting ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Filter')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddFilterModal;